// A race is a match whose selections are its runners.
//
// THE BOUNDARY RULE, and it is the whole design of the racing slice: racing is
// its own domain at ingest — this folder — and the SAME SHAPE as sport at the
// boundary. Nothing downstream of here learns a new noun. A race becomes a
// match keyed `racing_horse:2026-09-15:randwick:R5`, its `selections` are the
// active runners (`r4`, `r7`, …), and its `prices.r4` is the list of books
// paying for that runner. Ticket, timeline, History, Insights, share and the
// settle path then work unchanged, because from their side a leg carrying
// `side: 'r4'` is a leg like any other.
//
// So `fromRaces` answers the SAME canonical rows `fromOddsApiEvents` answers
// (src/lib/engine/universe.js) and `buildUniverse` folds them the same way.
// One row per race x active runner x bookmaker.
//
// THE RACE METADATA IS ON EVERY ROW, repeated. That is deliberate and it is
// temporary: `buildUniverse` builds a match from the FIRST row it sees for a
// key, and until R0's version carries a race bundle across, the only way a
// field reaches the match is to be on the row. Repeating it costs a few object
// references and makes this file provable on its own, today, without waiting
// for the engine.

// `bookmakers.js` rather than `odds.js` for the book names, and for the reason
// that file's own header gives: odds.js reaches for storage and the provider
// keys the moment it loads, and this module is a pure transform. The two name
// tables are pinned against each other by bookmakers.test.js, so borrowing the
// names here borrows the same names the board uses.
import { localDate } from '../tickets.js';
import { BOOKMAKER_SITES, bookLink } from '../bookmakers.js';
import { raceKey } from '../engine/teams.js';

/** Sydney, because a race meeting's date is the meeting's own, not the reader's. */
export const RACING_TZ = 'Australia/Sydney';

/**
 * The overround band a RACING vector may sum to and still be a market.
 *
 * Wider than the h2h band (`OVERROUND_BAND`, [0.85, 1.35]) in both directions
 * and for two different reasons. A fourteen-runner field carries far more
 * margin than a two-way market — 1.35 would refuse an ordinary race outright —
 * and the bottom of the band has to allow the genuine Dutch book that this app
 * exists to find. The engine reads it off the match (`overroundBandFor`, R0).
 */
export const RACING_OVERROUND_BAND = [0.95, 1.6];

/** PuntersEdge's category → our sport id. Anything else is not a race we carry. */
export const SPORT_FOR_CATEGORY = {
  horse: 'racing_horse',
  greyhound: 'racing_greyhound',
  harness: 'racing_harness',
};

/**
 * Racing books that are NOT on the sports board.
 *
 * Ten of PuntersEdge's fourteen keys are already `BOOKMAKERS` keys and are used
 * verbatim, so a racing price and a football price from Sportsbet land in one
 * column and one "Open at Sportsbet" button. These four do not price the sports
 * this app follows, so they have never needed a name until now.
 *
 * AN UNKNOWN KEY IS KEPT, never dropped. A price this app cannot put a name to
 * is still a price, and dropping it would quietly thin the consensus that the
 * whole slice exists to build — the one failure nobody would see on a screen.
 */
export const RACING_BOOKS = {
  palmerbet: 'Palmerbet',
  betdeluxe: 'BetDeluxe',
  boostbet: 'BoostBet',
  betgold: 'BetGold',
  // Not in the OpenAPI spec as of 2026-09-15; kept so a real Australian book
  // that turns up in the panel is named rather than title-cased.
  boombet: 'BoomBet',
};

/** `betr_au` → "Betr", `palmerbet` → "Palmerbet", anything else → "Some Book". */
export function racingBookmakerName(key) {
  const k = String(key ?? '');
  if (Object.hasOwn(BOOKMAKER_SITES, k)) return BOOKMAKER_SITES[k].name;
  if (Object.hasOwn(RACING_BOOKS, k)) return RACING_BOOKS[k];
  return k
    ? k.replace(/[_-]+/g, ' ').replace(/\b[a-z]/g, (c) => c.toUpperCase())
    : k;
}

// --- names, as a form guide prints them ---------------------------------------
// PuntersEdge shouts. Runners, venues, jockeys and the going arrive in capitals
// — "LEAF STRIDE", "MENANGLE", "GOOD 4" — because a tote board has one case and
// the feed is written off one. A leg reading "LEAF STRIDE (11) to win ·
// MENANGLE R1" on a pad where every football leg reads "Collingwood" is two
// apps on one screen, and the shouting reads as emphasis nobody meant.
//
// SO IT IS FIXED AT INGEST, here, once. Downstream there are a dozen places a
// name is printed — the leg, the ticket, the card, the race page, the share
// image, the paper trail, a settled record from three weeks ago — and a fix
// applied at any of them is a fix the others do not get. This is the boundary:
// what comes out of this file is what the app believes the runner is called.

/** Capitals that are the word: codes and countries, not names to be cased. */
const KEEP_CAPS = new Set([
  'NZ', 'AU', 'AUS', 'NSW', 'QLD', 'VIC', 'WA', 'SA', 'NT', 'ACT', 'UK', 'GB',
  'USA', 'US', 'HK', 'JPN', 'SGP', 'IRE', 'GER', 'FR', 'BM', 'TAB',
]);

/**
 * "III" is a name's tail, not a word — and "Ii" is not a thing.
 *
 * SPELLED OUT rather than "letters drawn from IVXLCDM", which is the obvious
 * expression and is wrong: MIX, MILL, CIVIL and DILL are all roman numerals by
 * that test, and a horse called MILL DAM would keep its capitals for ever. The
 * numerals a name actually carries stop at about twelve.
 */
const ROMAN = /^(?:I{2,3}|IV|VI{0,3}|IX|XI{0,2})$/;

/** One run of letters, cased: "mcdonald" → "McDonald", "reilly" → "Reilly". */
function capitalise(run) {
  // The Scots-Irish prefix, which is the one the shouting genuinely destroys:
  // "MCKENNA" title-cased word-by-word is "Mckenna", which is nobody's name.
  // Four letters is the floor so "Mc" alone and "Mca" are left where they are.
  if (run.startsWith('mc') && run.length >= 4) {
    return `Mc${run[2].toUpperCase()}${run.slice(3)}`;
  }
  return run[0].toUpperCase() + run.slice(1);
}

/**
 * One word of a shouted name.
 *
 * A WORD WITH A LOWERCASE LETTER IN IT IS LEFT ALONE. The feed is inconsistent
 * rather than uniformly shouted — "Leaf Stride" and "LEAF STRIDE" both arrive —
 * and a name somebody has already cased is a name this has no business
 * recasing: lowercasing "deSoto" or "van Dyke" would be inventing a spelling.
 */
function titleWord(word) {
  if (/[a-z]/.test(word)) return word;

  const lower = word.toLowerCase();
  let out = '';
  let i = 0;
  while (i < lower.length) {
    if (!/[a-z]/.test(lower[i])) { out += lower[i]; i += 1; continue; }
    let j = i;
    while (j < lower.length && /[a-z]/.test(lower[j])) j += 1;
    const run = lower.slice(i, j);
    const upper = run.toUpperCase();
    // The possessive, and only the possessive: a single letter directly after
    // an apostrophe. "O'REILLY'S GIRL" is "O'Reilly's Girl" — capitalising
    // every run after an apostrophe would print "O'Reilly'S", and dropping the
    // rule altogether would print "O'reilly's".
    const possessive = i > 0 && lower[i - 1] === '\'' && run.length === 1;
    // Tested on the RUN, not on the word, because the codes a name carries
    // arrive wearing brackets: "(NZ)" is one word and "NZ" is the thing to keep.
    if (KEEP_CAPS.has(upper) || ROMAN.test(upper)) out += upper;
    else out += possessive ? run : capitalise(run);
    i = j;
  }
  return out;
}

/**
 * "LEAF STRIDE" → "Leaf Stride", "O'REILLY'S GIRL" → "O'Reilly's Girl",
 * "MCKENNA (NZ)" → "McKenna (NZ)", "Leaf Stride" → "Leaf Stride".
 *
 * Digits, brackets, hyphens and apostrophes come through untouched, so a
 * saddlecloth, a country tag and a double-barrelled name all survive.
 */
export function titleCase(text) {
  const s = String(text ?? '');
  return s ? s.replace(/\S+/g, titleWord) : s;
}

/** Lowercase, no diacritics, alphanumerics joined by single hyphens. */
function slug(name) {
  return String(name ?? '')
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The slug a race's key is built on.
 *
 * `venue_id` FIRST, because the vendor guarantees that spelling variants of one
 * venue share one id — the payload has arrived as "MONMORE" and as "Monmore"
 * on consecutive pulls, and a key built off the display name would have made
 * those two different races and lost every bet struck on the first one.
 */
export function venueSlug(race) {
  return slug(race?.venue_id) || slug(race?.venue_canonical) || slug(race?.venue);
}

/** "Randwick R5". What a leg prints as its `match`. */
export function raceLabel(match) {
  const venue = match?.venue ?? '';
  const n = match?.raceNumber;
  return n === null || n === undefined ? String(venue) : `${venue} R${n}`;
}

/** "Winx (4)". The " to win" tail belongs to whoever is writing the leg. */
export function runnerLabel(match, selection) {
  const runner = match?.runners?.[selection];
  if (!runner) return String(selection ?? '');
  return runner.number === null || runner.number === undefined
    ? String(runner.name ?? selection)
    : `${runner.name} (${runner.number})`;
}

/** `4` → `'r4'`. The selection id a leg carries. */
export const runnerId = (number) => `r${number}`;

const finite = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
};

/** One runner, in the shape the match carries and RacePage prints. */
function runnerRow(raw, { scratched }) {
  return {
    // Cased here rather than where it prints: every screen, every share card
    // and every record written from today onwards reads this one field.
    name: titleCase(raw?.name ?? ''),
    number: finite(raw?.number),
    barrier: finite(raw?.barrier),
    jockey: raw?.jockey ? titleCase(raw.jockey) : null,
    trainer: raw?.trainer ? titleCase(raw.trainer) : null,
    weight: finite(raw?.weight),
    form: raw?.form ?? null,
    scratched,
  };
}

/**
 * PuntersEdge `NextRaceOut[]` → canonical rows.
 *
 * `asOf` is what every row is captured at, exactly as `fromOddsApiEvents` uses
 * it: the fetch's own instant, not the clock, so a card read twenty minutes
 * later still says when its prices were bought.
 *
 * A race is dropped only when it cannot be identified — no runners, no venue,
 * no race number, or a category this app does not carry. Everything else comes
 * through, jumped races included: `buildUniverse` is where a race that has
 * already run gets cut, on the same `kickoff > asOf` rule every sport is cut
 * on, and the board (R3) wants the jumped ones to dim rather than vanish.
 */
export function fromRaces(races, { asOf } = {}) {
  const rows = [];
  for (const race of races ?? []) {
    const sport = SPORT_FOR_CATEGORY[String(race?.category ?? '').toLowerCase()];
    const slugged = venueSlug(race);
    const raceNumber = finite(race?.race_number);
    if (!sport || !slugged || raceNumber === null || !race?.start_time) continue;

    // SCRATCHINGS ARE NEVER IN `runners`. The upstream splits them out — even
    // while a book is still publishing a price for a scratched runner — so the
    // active field falls out of the payload rather than being derived here.
    // They go into `runners` carrying `scratched: true`, because RacePage
    // prints them at the foot struck through, and they stay OUT of
    // `selections`, because a book that prices the active field has voted.
    const runners = {};
    const selections = [];
    for (const raw of race.runners ?? []) {
      const number = finite(raw?.number);
      if (number === null) continue;
      const id = runnerId(number);
      if (Object.hasOwn(runners, id)) continue;
      runners[id] = runnerRow(raw, { scratched: false });
      selections.push(id);
    }
    for (const raw of race.scratchings ?? []) {
      const number = finite(raw?.number);
      if (number === null) continue;
      const id = runnerId(number);
      if (Object.hasOwn(runners, id)) continue;
      runners[id] = runnerRow(raw, { scratched: true });
    }
    if (!selections.length) continue;

    // The venue is cased once, here, and every label built below is built from
    // it — `raceLabel`, the meeting a card groups under, the leg's own "·
    // Menangle R1". The SLUG is taken from the raw fields above and is
    // unaffected: it lowercases everything it is given, so a key struck this
    // morning and a key struck after this change are the same key.
    const venue = titleCase(race.venue_canonical ?? race.venue ?? slugged);
    const kickoff = race.start_time;
    // THE ENGINE'S OWN KEY, not a copy of its expression. `raceKey` reads the
    // meeting's timezone off `SPORTS[sport].tz` — which is Sydney for all three
    // codes — so a race keyed here and a race keyed anywhere else in the app
    // cannot drift by a date boundary.
    const matchKey = raceKey(sport, kickoff, slugged, raceNumber);
    // The meeting, which is what `competition` means everywhere else: every
    // race at one venue on one day groups under one heading.
    const competition = `${venue} ${localDate(kickoff, RACING_TZ)}`;

    const race_meta = {
      selections,
      runners,
      overroundBand: RACING_OVERROUND_BAND,
      raceLabel: `${venue} R${raceNumber}`,
      venue,
      venueId: slugged,
      raceNumber,
      // NOT CASED, and deliberately. A race name is a sponsor's line with the
      // conditions abbreviated into it — "TEAM AIDAN MARKETS @ CRJC 22 NOVEMBER
      // CB MDN PLT" — and casing it gives "Crjc … Mdn Plt", which is worse than
      // the shouting. Every form guide in the country prints these in capitals.
      raceName: race.race_name ?? null,
      distance: finite(race.distance_m),
      // "GOOD 4" → "Good 4", which is how the going is written everywhere but
      // on a tote board, and how `raceRow`'s meta line already assumed it read.
      trackCondition: race.track_condition ? titleCase(race.track_condition) : null,
      placesPaid: finite(race.places_paid),
      marketClosedAt: race.market_closed_at ?? null,
      inplayAt: race.inplay_at ?? null,
      category: race.category ?? null,
      country: race.country ?? null,
    };

    for (const raw of race.runners ?? []) {
      const number = finite(raw?.number);
      if (number === null) continue;
      const selection = runnerId(number);
      for (const book of raw?.bookmakers ?? []) {
        const price = finite(book?.win_price);
        if (price === null || price <= 1) continue;
        const link = bookLink(book.key, book.source_url);
        rows.push({
          matchKey,
          providerEventId: race.race_id ?? matchKey,
          sport,
          competition,
          kickoff,
          // A race has no sides. `sideName` (R0) reads `match.runners[...]`
          // before it reaches for these, so they are null rather than invented.
          home: null,
          away: null,
          selection,
          bookmaker: book.key,
          bookmakerName: racingBookmakerName(book.key),
          price,
          // THE BOOK'S OWN PAGE FOR THIS RACE, where it published one.
          //
          // `source_url` is per bookmaker per runner in the feed and it is the
          // RACE's page at that book — the TAB card, the PointsBet meeting, the
          // Ladbrokes/Neds race id — never a bet slip, so `linkKind` is 'event'
          // and the button keeps saying "Open at TAB". It is checked here, at
          // the boundary, against that book's own domain (`bookLink`): a URL
          // this app will open is a URL somebody clicked a button in this app
          // to reach. A racing-only shop has no address in `BOOKMAKER_SITES`
          // and so carries no link, which costs nothing — the handoff can only
          // open a book it has an address for anyway.
          ...(link ? { link, linkKind: 'event' } : {}),
          liquidity: null,
          capturedAt: asOf,
          kind: 'live',
          ...race_meta,
        });
      }
    }
  }
  return rows;
}
